import { lazy, Suspense } from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Info, Lightbulb } from 'lucide-react';
import type {
  SlideCalloutTone,
  SlideContentBlock,
  SlideImageSize,
  SlideLeafBlock,
} from '@hcc/types';
import { LinkifiedText } from '../../ui/LinkifiedText';
import { SlideRichText } from './SlideRichText';
import { VideoViewer } from './VideoViewer';
import type { WatchGate } from './videoGate';

// pdfjs is heavy; only load it when a slide actually embeds a PDF.
const PdfViewer = lazy(() => import('../../pdf/PdfViewer'));

const CALLOUT_CLASSES: Record<SlideCalloutTone, string> = {
  info: 'bg-info-soft/15 text-info-soft-fg',
  tip: 'bg-accent/10 text-accent',
  success: 'bg-success-soft/15 text-success-soft-fg',
  warning: 'bg-warning-soft/15 text-warning-soft-fg',
  danger: 'bg-danger-soft/15 text-danger-soft-fg',
};

const CALLOUT_ICONS: Record<SlideCalloutTone, typeof Info> = {
  info: Info,
  tip: Lightbulb,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: CircleAlert,
};

const IMAGE_HEIGHTS: Record<SlideImageSize, string> = {
  small: 'max-h-40',
  medium: 'max-h-80',
  large: 'max-h-[32rem]',
};

const IMAGE_ALIGNS: Record<'left' | 'center' | 'right', string> = {
  left: 'items-start',
  center: 'items-center',
  right: 'items-end',
};

/** Renders a block-based slide body. Text stays text nodes throughout. */
export function SlideBlocks({
  blocks,
  watch,
}: {
  blocks: SlideContentBlock[];
  watch?: WatchGate;
}) {
  return (
    <div className="space-y-4">
      {blocks.map((block) =>
        block.type === 'columns' ? (
          <div key={block.id} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-4">
              {block.left.map((b) => (
                <LeafBlock key={b.id} block={b} watch={watch} />
              ))}
            </div>
            <div className="min-w-0 space-y-4">
              {block.right.map((b) => (
                <LeafBlock key={b.id} block={b} watch={watch} />
              ))}
            </div>
          </div>
        ) : (
          <LeafBlock key={block.id} block={block} watch={watch} />
        ),
      )}
    </div>
  );
}

function LeafBlock({ block, watch }: { block: SlideLeafBlock; watch?: WatchGate }) {
  switch (block.type) {
    case 'heading':
      return (
        <h3 className="pt-1 text-base font-semibold text-fg">
          <LinkifiedText text={block.text} />
        </h3>
      );
    case 'text':
      // Text blocks keep the body markup subset, so inline lists,
      // callout lines and bold all still work inside them.
      return <SlideRichText body={block.text} />;
    case 'list': {
      const Tag = block.style === 'numbered' ? 'ol' : 'ul';
      // The editor keeps blank lines while typing; never render them.
      const items = block.items.filter((item) => item.trim() !== '');
      return (
        <Tag
          className={`${block.style === 'numbered' ? 'list-decimal' : 'list-disc'} space-y-1.5 pl-6 text-sm leading-relaxed text-fg`}
        >
          {items.map((item, i) => (
            <li key={i}>
              <LinkifiedText text={item} />
            </li>
          ))}
        </Tag>
      );
    }
    case 'callout': {
      const Icon = CALLOUT_ICONS[block.tone];
      return (
        <div
          className={`flex items-start gap-2 rounded-token-sm px-3 py-2.5 text-sm leading-relaxed ${CALLOUT_CLASSES[block.tone]}`}
        >
          <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <LinkifiedText text={block.text} />
          </span>
        </div>
      );
    }
    case 'image':
      return (
        <figure className={`flex flex-col ${IMAGE_ALIGNS[block.align ?? 'left']}`}>
          <img
            src={block.image_key}
            alt={block.alt ?? ''}
            className={`rounded-token-sm border border-line/10 ${
              block.size === 'full'
                ? 'w-full'
                : `max-w-full ${IMAGE_HEIGHTS[block.size ?? 'medium']}`
            }`}
          />
          {block.caption ? (
            <figcaption className="mt-1.5 text-xs text-fg-muted">{block.caption}</figcaption>
          ) : null}
        </figure>
      );
    case 'pdf':
      return (
        <figure>
          <Suspense fallback={<p className="text-sm text-fg-muted">Loading PDF…</p>}>
            <PdfViewer url={block.file_key} />
          </Suspense>
          {block.caption ? (
            <figcaption className="mt-1.5 text-xs text-fg-muted">{block.caption}</figcaption>
          ) : null}
        </figure>
      );
    case 'video':
      return (
        <figure>
          <VideoViewer
            url={block.file_key}
            gated={!!watch}
            watched={watch?.isWatched(block.id) ?? false}
            onWatched={watch ? () => watch.onWatched(block.id) : undefined}
          />
          {block.caption ? (
            <figcaption className="mt-1.5 text-xs text-fg-muted">{block.caption}</figcaption>
          ) : null}
        </figure>
      );
    case 'quote':
      return (
        <blockquote className="border-l-2 border-accent pl-4">
          <p className="text-sm italic leading-relaxed text-fg">
            <LinkifiedText text={block.text} />
          </p>
          {block.attribution ? (
            <footer className="mt-1 text-xs text-fg-muted">— {block.attribution}</footer>
          ) : null}
        </blockquote>
      );
    case 'statement':
      return (
        <p className="py-6 text-center text-2xl font-semibold leading-snug text-fg">
          <LinkifiedText text={block.text} />
        </p>
      );
    case 'table':
      return (
        <div className="overflow-x-auto rounded-token-sm border border-line/10">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-surface-2 text-xs uppercase tracking-wide text-fg-muted">
              <tr>
                {block.header.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-medium">
                    <LinkifiedText text={h} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-divider/10">
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 text-fg">
                      <LinkifiedText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'divider':
      return <hr className="border-line/10" />;
  }
}
